import React, { useState, useEffect, useMemo } from 'react';
import api, { questionAPI, employerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { downloadResumeAuthenticated } from '../utils/resumeUrl';

// Helper function to get Indian IT cities
const getIndianITCities = () => {
  return [
    'All Locations',
    'Bangalore',
    'Hyderabad',
    'Pune',
    'Chennai',
    'Mumbai',
    'Delhi',
    'Noida',
    'Gurgaon',
    'Kolkata',
    'Ahmedabad',
    'Jaipur',
    'Chandigarh',
    'Indore',
    'Coimbatore',
    'Kochi',
    'Trivandrum',
    'Bhubaneswar',
    'Vadodara',
    'Nagpur',
    'Mysore',
    'Remote'
  ];
};

const CandidateList = ({ jobId = null, onJobIdClear = null }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState({}); // Store details by candidate ID
  const [loadingCandidateDetails, setLoadingCandidateDetails] = useState({}); // Track loading per candidate
  const [assessmentScores, setAssessmentScores] = useState({});
  const [loadingAssessments, setLoadingAssessments] = useState({});
  const [jobTitle, setJobTitle] = useState('');
  
  // Job filter states
  const [selectedJobId, setSelectedJobId] = useState(jobId || null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Filter states
  const [filterLocation, setFilterLocation] = useState('');
  const [filterJoiningDateFrom, setFilterJoiningDateFrom] = useState('');
  const [filterJoiningDateTo, setFilterJoiningDateTo] = useState('');
  const [filterSalaryMin, setFilterSalaryMin] = useState('');
  const [filterSalaryMax, setFilterSalaryMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Candidate detail request states
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [usageInfo, setUsageInfo] = useState({ planLimit: 30, usedCount: 0, remainingCount: 30 });
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [requestStatuses, setRequestStatuses] = useState({}); // candidateId -> {status, request_id, ...}
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  
  const pageSize = 10;

  // Fetch jobs for filter dropdown
  useEffect(() => {
    fetchJobs();
  }, []);

  // Update selectedJobId when jobId prop changes
  useEffect(() => {
    if (jobId) {
      setSelectedJobId(jobId);
    }
  }, [jobId]);

  useEffect(() => {
    fetchCandidates();
  }, [page, search, selectedJobId]);

  // Fetch request statuses when candidates change
  useEffect(() => {
    if (candidates.length > 0) {
      fetchRequestStatuses();
    }
  }, [candidates]);

  // Auto-fetch full details for approved candidates
  useEffect(() => {
    const fetchApprovedDetails = async () => {
      for (const candidate of candidates) {
        const requestStatus = requestStatuses[candidate.id];
        if (requestStatus && requestStatus.status === 'approved') {
          // Fetch basic profile first if not loaded
          if (!candidateDetails[candidate.id]) {
            await fetchBasicCandidateProfile(candidate.id);
          }
          // Then fetch full details
          await fetchFullCandidateDetails(candidate.id);
        }
      }
    };
    
    if (candidates.length > 0 && Object.keys(requestStatuses).length > 0) {
      fetchApprovedDetails();
    }
  }, [candidates, requestStatuses]);

  // Fetch usage info
  useEffect(() => {
    fetchUsageInfo();
  }, []);

  const fetchUsageInfo = async () => {
    try {
      setLoadingUsage(true);
      const response = await employerAPI.getCandidateDetailUsage();
      setUsageInfo(response.data);
    } catch (error) {
      console.error('Error fetching usage info:', error);
      // Use defaults on error
      setUsageInfo({ planLimit: 30, usedCount: 0, remainingCount: 30 });
    } finally {
      setLoadingUsage(false);
    }
  };

  const fetchRequestStatuses = async () => {
    try {
      setLoadingStatuses(true);
      const candidateIds = candidates.map(c => c.id);
      if (candidateIds.length === 0) return;
      
      const response = await employerAPI.getCandidateRequestStatuses(candidateIds);
      setRequestStatuses(response.data || {});
    } catch (error) {
      console.error('Error fetching request statuses:', error);
      setRequestStatuses({});
    } finally {
      setLoadingStatuses(false);
    }
  };

  // Fetch assessment history for candidates
  useEffect(() => {
    const fetchAssessmentScores = async () => {
      for (const candidate of candidates) {
        if (!candidate.userId || assessmentScores[candidate.id] || loadingAssessments[candidate.id]) {
          continue;
        }
        
        setLoadingAssessments(prev => ({ ...prev, [candidate.id]: true }));
        try {
          // Fetch both available assessments and assessment history
          // getAssessmentsByDifficulty endpoint uses user ID, getAssessmentHistory uses candidate ID
          const [availableResponse, historyResponse] = await Promise.all([
            // Uses user ID (based on Dashboard.js pattern)
            questionAPI.getAssessmentsByDifficulty(candidate.userId).catch((err) => {
              console.warn(`Failed to fetch available assessments for candidate ${candidate.id} (userId: ${candidate.userId}):`, err);
              return { data: { assessments_by_difficulty: {} } };
            }),
            // Uses candidate ID (endpoint path: /assessments/candidate/{candidateId}/history)
            questionAPI.getAssessmentHistory(candidate.id).catch((err) => {
              console.warn(`Failed to fetch assessment history for candidate ${candidate.id}:`, err);
              return { data: { assessments: [] } };
            })
          ]);
          
          // Calculate total available assessments (all levels)
          const assessmentsByDifficulty = availableResponse.data?.assessments_by_difficulty || {};
          let totalAssessments = (assessmentsByDifficulty.beginner?.length || 0) + 
                                   (assessmentsByDifficulty.intermediate?.length || 0) + 
                                   (assessmentsByDifficulty.advanced?.length || 0);
          
          // Get completed assessments from history
          const allAssessments = historyResponse.data?.assessments || [];
          const completedAssessments = allAssessments.filter(assessment => {
            // Check multiple conditions for completed status
            return assessment.status === 'completed' || 
                   assessment.status === 'Completed' ||
                   assessment.passed === true || 
                   assessment.completed_at ||
                   (assessment.completedAt && assessment.completedAt !== null);
          });
          
          const completedCount = completedAssessments.length;
          
          // Count unique assessment IDs from history to get actual total assessments attempted/completed
          // This is more accurate than relying on available assessments endpoint which might return empty
          const uniqueAssessmentIds = new Set();
          allAssessments.forEach(a => {
            const assessmentId = a.assessment_id || a.assessmentId;
            if (assessmentId) uniqueAssessmentIds.add(assessmentId);
          });
          
          // Use the larger value: available assessments count OR unique assessments from history
          // This ensures we show the correct total (available + completed)
          const historyTotal = uniqueAssessmentIds.size;
          totalAssessments = Math.max(totalAssessments, historyTotal);
          
          console.log(`Candidate ${candidate.id} assessments:`, {
            totalAssessments,
            completedCount,
            allAssessments: allAssessments.length,
            availableData: assessmentsByDifficulty,
            historyData: allAssessments
          });
          
          setAssessmentScores(prev => ({
            ...prev,
            [candidate.id]: {
              totalAssessments,
              completedCount,
              assessments: completedAssessments
            }
          }));
        } catch (error) {
          console.error(`Error fetching assessment data for candidate ${candidate.id} (userId: ${candidate.userId}):`, error);
          setAssessmentScores(prev => ({
            ...prev,
            [candidate.id]: {
              totalAssessments: 0,
              completedCount: 0,
              assessments: []
            }
          }));
        } finally {
          setLoadingAssessments(prev => {
            const newState = { ...prev };
            delete newState[candidate.id];
            return newState;
          });
        }
      }
    };
    
    if (candidates.length > 0) {
      fetchAssessmentScores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await employerAPI.getJobPostings();
      const jobsData = response.data?.jobs || response.data?.data?.jobs || response.data?.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchCandidates = async () => {
    // If selectedJobId is provided, fetch matched candidates for that job
    if (selectedJobId) {
      try {
        setLoading(true);
        const response = await employerAPI.getMatchedCandidates(selectedJobId, page, pageSize, 0);
        const data = response.data;
        
        // Transform matched candidates to match component structure
        const matchedCandidates = (data.candidates || []).map(candidate => ({
          id: String(candidate.candidateId || candidate.candidate_id || ''),
          userId: String(candidate.candidateId || candidate.candidate_id || ''),
          name: candidate.candidateName || candidate.candidate_name || 'Unknown',
          email: candidate.candidateEmail || candidate.candidate_email || '',
          matchScore: candidate.matchScore || candidate.match_score || 0,
          skillsMatchScore: candidate.skillsMatchScore || candidate.skills_match_score || 0,
          assessmentMatchScore: candidate.assessmentMatchScore || candidate.assessment_match_score || 0,
          matchedSkills: candidate.matchedSkills || candidate.matched_skills || [],
          missingSkills: candidate.missingSkills || candidate.missing_skills || [],
          totalAssessments: candidate.totalAssessments || candidate.total_assessments || 0,
          averageAssessmentScore: candidate.averageAssessmentScore || candidate.average_assessment_score || null,
          assessmentScores: candidate.assessmentScores || candidate.assessment_scores || []
        }));
        
        setCandidates(matchedCandidates);
        setTotal(data.total || 0);
        setJobTitle(data.jobTitle || data.job_title || '');
      } catch (err) {
        console.error('Error fetching matched candidates:', err);
        toast.error('Failed to load matched candidates');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Otherwise, fetch all candidates (existing logic)
    try {
      setLoading(true);
      // Get all candidates (minimal details) for employers
      const response = await api.get('/employers/candidates', {
        params: { 
          page, 
          page_size: pageSize,
          search: search.trim() || undefined
        }
      });
      
      const data = response.data || {};
      const candidatesList = data.candidates || [];
      
      // Transform to match component structure
      const transformedCandidates = candidatesList.map(candidate => ({
        id: String(candidate.id || ''),
        userId: String(candidate.userId || candidate.user_id || ''),
        name: `${candidate.firstName || candidate.first_name || ''} ${candidate.lastName || candidate.last_name || ''}`.trim() || candidate.email || 'Unknown',
        email: String(candidate.email || ''),
        firstName: String(candidate.firstName || candidate.first_name || ''),
        lastName: String(candidate.lastName || candidate.last_name || ''),
        phone: candidate.phone ? String(candidate.phone) : null,
        title: candidate.title ? String(candidate.title) : null,
        address: candidate.address ? String(candidate.address) : null,
        location: candidate.location || candidate.address || null,
        expectedSalary: candidate.expectedSalary || candidate.expected_salary || null,
        availabilityDate: candidate.availabilityDate || candidate.availability_date || candidate.joiningDate || candidate.joining_date || null,
        experienceYears: candidate.experienceYears || candidate.experience_years || 0,
        profileCompletion: candidate.profileCompletion || candidate.profile_completion_percentage || 0
      }));
      
      setCandidates(transformedCandidates);
      setTotal(data.total || 0);
      
      // Reset to page 1 if current page is beyond available pages
      if (data.totalPages && page > data.totalPages && data.totalPages > 0) {
        setPage(1);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      
      // Extract error message properly - handle objects, arrays, and strings
      let errorMessage = 'Failed to load candidates';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation error arrays
          errorMessage = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof detail === 'object') {
          // Handle single validation error object
          if (detail.msg) {
            errorMessage = detail.msg;
          } else {
            errorMessage = JSON.stringify(detail);
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage
      });
      toast.error(errorMessage);
      setCandidates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getCandidateName = (candidate) => {
    if (candidate.name) return candidate.name;
    if (candidate.firstName && candidate.lastName) {
      return `${candidate.firstName} ${candidate.lastName}`;
    }
    if (candidate.firstName) return candidate.firstName;
    if (candidate.lastName) return candidate.lastName;
    if (candidate.email) {
      const emailName = candidate.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return 'Unknown Candidate';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  // Filter candidates based on filter criteria
  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      // Location filter
      if (filterLocation) {
        const location = (candidate.location || candidate.address || '').toLowerCase();
        if (!location.includes(filterLocation.toLowerCase())) {
          return false;
        }
      }
      
      // Joining date filter
      if (filterJoiningDateFrom || filterJoiningDateTo) {
        if (!candidate.availabilityDate) return false;
        const candidateDate = new Date(candidate.availabilityDate);
        if (filterJoiningDateFrom) {
          const fromDate = new Date(filterJoiningDateFrom);
          if (candidateDate < fromDate) return false;
        }
        if (filterJoiningDateTo) {
          const toDate = new Date(filterJoiningDateTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire end date
          if (candidateDate > toDate) return false;
        }
      }
      
      // Expected salary filter
      if (filterSalaryMin || filterSalaryMax) {
        const salary = candidate.expectedSalary;
        if (!salary) return false;
        const salaryNum = typeof salary === 'number' ? salary : parseFloat(salary);
        if (isNaN(salaryNum)) return false;
        if (filterSalaryMin && salaryNum < parseFloat(filterSalaryMin)) return false;
        if (filterSalaryMax && salaryNum > parseFloat(filterSalaryMax)) return false;
      }
      
      return true;
    });
  }, [candidates, filterLocation, filterJoiningDateFrom, filterJoiningDateTo, filterSalaryMin, filterSalaryMax]);

  const clearFilters = () => {
    setFilterLocation('');
    setFilterJoiningDateFrom('');
    setFilterJoiningDateTo('');
    setFilterSalaryMin('');
    setFilterSalaryMax('');
  };

  const hasActiveFilters = selectedJobId || filterLocation || filterJoiningDateFrom || filterJoiningDateTo || filterSalaryMin || filterSalaryMax;

  // Handle candidate selection
  const handleCandidateToggle = (candidateId) => {
    // Check if candidate already has a pending or approved request
    const requestStatus = requestStatuses[candidateId];
    if (requestStatus && (requestStatus.status === 'pending' || requestStatus.status === 'approved')) {
      toast.error(`This candidate already has a ${requestStatus.status} request. You cannot request again.`);
      return;
    }
    
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      // Check if we can select more
      if (newSelected.size >= usageInfo.remainingCount) {
        toast.error(`You can only select up to ${usageInfo.remainingCount} more candidate(s). Your plan limit is ${usageInfo.planLimit} and you have used ${usageInfo.usedCount}.`);
        return;
      }
      newSelected.add(candidateId);
    }
    setSelectedCandidates(newSelected);
  };

  // Fetch basic candidate profile (always available)
  const fetchBasicCandidateProfile = async (candidateId) => {
    // Check if already loaded (but allow refresh if needed)
    const existingData = candidateDetails[candidateId];
    if (existingData && existingData.id) {
      // Only return if we have basic data, but allow fetching full details
      console.log('Basic profile already loaded for candidate:', candidateId);
      return existingData;
    }

    try {
      setLoadingCandidateDetails(prev => ({ ...prev, [candidateId]: true }));
      // Use public endpoint for basic profile
      const response = await api.get(`/candidates/${candidateId}`);
      const basicData = response.data || {};
      
      console.log('Basic profile fetched:', basicData);
      
      // Store basic data (merge with existing if any)
      setCandidateDetails(prev => {
        const existing = prev[candidateId] || {};
        return { ...prev, [candidateId]: { ...existing, ...basicData } };
      });
      return basicData;
    } catch (error) {
      console.error('Error fetching basic candidate profile:', error);
      toast.error('Failed to load candidate profile');
      return null;
    } finally {
      setLoadingCandidateDetails(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  // Fetch full candidate details (only when approved)
  const fetchFullCandidateDetails = async (candidateId) => {
    try {
      setLoadingCandidateDetails(prev => ({ ...prev, [candidateId]: true }));
      const response = await employerAPI.getCandidateDetails(candidateId);
      console.log('Full candidate details response:', response);
      console.log('Full candidate details data:', response.data);
      console.log('Resume URL in response:', response.data?.resumeUrl);
      console.log('Email in response:', response.data?.candidateEmail);
      console.log('Phone in response:', response.data?.phone);
      
      if (!response || !response.data) {
        console.error('No data in response');
        return null;
      }
      
      // Merge with existing basic data (full details override basic)
      const existingData = candidateDetails[candidateId] || {};
      const fullData = { 
        ...existingData, 
        ...response.data,
        // Ensure these fields are set from the full details response (use response data first)
        resumeUrl: response.data.resumeUrl || response.data.resume_url || existingData.resumeUrl,
        candidateEmail: response.data.candidateEmail || response.data.candidate_email || existingData.candidateEmail,
        candidateName: response.data.candidateName || response.data.candidate_name || existingData.candidateName,
        phone: response.data.phone || existingData.phone,
        location: response.data.location || response.data.address || existingData.location
      };
      
      console.log('Merged full data:', fullData);
      console.log('Resume URL after merge:', fullData.resumeUrl);
      console.log('Email after merge:', fullData.candidateEmail);
      console.log('Phone after merge:', fullData.phone);
      
      setCandidateDetails(prev => ({ ...prev, [candidateId]: fullData }));
      return fullData;
    } catch (error) {
      console.error('Error fetching full candidate details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Extract error message properly
      let errorMessage = 'Failed to load candidate details';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      // Don't show error if it's just not approved - that's expected
      if (error.response?.status !== 403) {
        toast.error(errorMessage);
      } else {
        console.log('Access denied (403) - request may not be approved yet');
      }
      return null;
    } finally {
      setLoadingCandidateDetails(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  const handleViewCandidateDetails = async (candidate) => {
    setSelectedCandidate(candidate);
    
    // Check if request is approved
    const requestStatus = requestStatuses[candidate.id];
    const isApproved = requestStatus && requestStatus.status === 'approved';
    
    console.log('View Details clicked for candidate:', candidate.id);
    console.log('Request status:', requestStatus);
    console.log('Is approved:', isApproved);
    
    // Always fetch basic profile first
    await fetchBasicCandidateProfile(candidate.id);
    
    // If approved, also fetch full details (resume, contact info)
    if (isApproved) {
      console.log('Request is approved, fetching full details for candidate:', candidate.id);
      await fetchFullCandidateDetails(candidate.id);
    } else {
      console.log('Request not approved or no request. Status:', requestStatus?.status);
    }
  };

  const handleRequestDetails = async () => {
    if (selectedCandidates.size === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    if (selectedCandidates.size > usageInfo.remainingCount) {
      toast.error(`You can only request ${usageInfo.remainingCount} more candidate details.`);
      return;
    }

    setRequesting(true);
    try {
      // Include jobId if filtering by job
      const requestData = {
        candidateIds: Array.from(selectedCandidates),
        jobId: selectedJobId || null
      };
      const response = await employerAPI.createCandidateDetailRequests(requestData);
      toast.success(response.data.message || `Successfully requested details for ${selectedCandidates.size} candidate(s)`);
      setSelectedCandidates(new Set());
      await fetchUsageInfo();
      // Refresh request statuses after creating requests
      await fetchRequestStatuses();
    } catch (error) {
      console.error('Error requesting candidate details:', error);
      
      // Extract error message properly - handle objects, arrays, and strings
      let errorMessage = 'Failed to request candidate details';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation error arrays
          errorMessage = detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof detail === 'object') {
          // Handle single validation error object
          if (detail.msg) {
            errorMessage = detail.msg;
          } else {
            errorMessage = JSON.stringify(detail);
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setRequesting(false);
    }
  };

  if (loading && candidates.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Job Filter Header */}
      {selectedJobId && jobTitle && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-900">Viewing Matched Candidates</p>
              <p className="text-xs text-blue-700">Job: {jobTitle}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedJobId(null);
              setJobTitle('');
              if (onJobIdClear) {
                onJobIdClear();
              }
            }}
            className="px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
          >
            Clear Filter
          </button>
        </div>
      )}
      
      {/* Search Bar and Filters */}
      <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 relative">
            <svg
              className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search candidates by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // Reset to first page on search
              }}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          {/* Job Title Filter Dropdown (Quick Access) */}
          <div className="w-64">
            <select
              value={selectedJobId || ''}
              onChange={(e) => {
                const jobId = e.target.value || null;
                setSelectedJobId(jobId);
                setPage(1);
                // Find job title for display
                if (jobId) {
                  const selectedJob = jobs.find(j => j.id === jobId);
                  setJobTitle(selectedJob?.title || '');
                } else {
                  setJobTitle('');
                }
              }}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
              disabled={loadingJobs}
            >
              <option value="">All Jobs (All Candidates)</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 text-sm ${
              showFilters || hasActiveFilters
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            More Filters
            {hasActiveFilters && (
              <span className="ml-0.5 bg-primary-600 text-white text-xs rounded-full px-1.5 py-0.5">
                Active
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="border-t border-gray-200 pt-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Location Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  value={filterLocation}
                  onChange={(e) => {
                    const value = e.target.value === 'All Locations' ? '' : e.target.value;
                    setFilterLocation(value);
                    setPage(1);
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  {getIndianITCities().map((city) => (
                    <option key={city} value={city === 'All Locations' ? '' : city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              {/* Joining Date Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Joining Date From
                </label>
                <input
                  type="date"
                  value={filterJoiningDateFrom}
                  onChange={(e) => {
                    setFilterJoiningDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Joining Date To
                </label>
                <input
                  type="date"
                  value={filterJoiningDateTo}
                  onChange={(e) => {
                    setFilterJoiningDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Expected Salary Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Expected Salary (Min)
                </label>
                <input
                  type="number"
                  placeholder="Min salary..."
                  value={filterSalaryMin}
                  onChange={(e) => {
                    setFilterSalaryMin(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Expected Salary (Max)
                </label>
                <input
                  type="number"
                  placeholder="Max salary..."
                  value={filterSalaryMax}
                  onChange={(e) => {
                    setFilterSalaryMax(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {filteredCandidates.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No candidates found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {search
              ? 'No candidates match your search criteria.'
              : 'No candidates found in the system.'}
          </p>
        </div>
      ) : (
        <>
          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium text-yellow-800">
                  {loadingUsage ? 'Loading...' : `You have ${usageInfo.remainingCount} candidate detail view(s) remaining (Plan Limit: ${usageInfo.planLimit}, Used: ${usageInfo.usedCount})`}
                </span>
              </div>
              {selectedCandidates.size > 0 && (
                <button
                  onClick={handleRequestDetails}
                  disabled={requesting || selectedCandidates.size > usageInfo.remainingCount}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {requesting ? 'Requesting...' : `Request Details (${selectedCandidates.size})`}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {filteredCandidates.map((candidate) => {
              const score = assessmentScores[candidate.id];
              const isLoadingScore = loadingAssessments[candidate.id];
              
              return (
                <div
                  key={candidate.id}
                  className="ui-card-interactive p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 mr-4">
                      {(() => {
                        const requestStatus = requestStatuses[candidate.id];
                        const hasExistingRequest = requestStatus && (requestStatus.status === 'pending' || requestStatus.status === 'approved');
                        const isDisabled = hasExistingRequest || (!selectedCandidates.has(candidate.id) && selectedCandidates.size >= usageInfo.remainingCount);
                        
                        return (
                          <input
                            type="checkbox"
                            checked={selectedCandidates.has(candidate.id)}
                            onChange={() => handleCandidateToggle(candidate.id)}
                            disabled={isDisabled}
                            className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={hasExistingRequest ? `Request already ${requestStatus.status}` : ''}
                          />
                        );
                      })()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-semibold text-lg">
                            {getCandidateName(candidate).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 truncate">
                            {getCandidateName(candidate)}
                          </h3>
                          {selectedJobId && candidate.email && (
                            <p className="text-sm text-gray-600 mt-1">{candidate.email}</p>
                          )}
                          {candidate.title && (
                            <p className="text-sm text-gray-700 mt-1">{candidate.title}</p>
                          )}
                          {(candidate.location || candidate.address) && (
                            <p className="text-sm text-gray-600 mt-1">{candidate.location || candidate.address}</p>
                          )}
                          
                          {/* Match Score Information (for matched candidates) */}
                          {selectedJobId && (candidate.matchScore !== undefined || candidate.skillsMatchScore !== undefined) && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                {candidate.matchScore !== undefined && (
                                  <div>
                                    <div className="text-xs text-blue-700 font-medium mb-1">Overall Match</div>
                                    <div className="text-lg font-bold text-blue-900">{Math.round(candidate.matchScore)}%</div>
                                  </div>
                                )}
                                {candidate.skillsMatchScore !== undefined && (
                                  <div>
                                    <div className="text-xs text-blue-700 font-medium mb-1">Skills Match</div>
                                    <div className="text-lg font-bold text-blue-900">{Math.round(candidate.skillsMatchScore)}%</div>
                                  </div>
                                )}
                                {candidate.assessmentMatchScore !== undefined && (
                                  <div>
                                    <div className="text-xs text-blue-700 font-medium mb-1">Assessment Match</div>
                                    <div className="text-lg font-bold text-blue-900">{Math.round(candidate.assessmentMatchScore)}%</div>
                                  </div>
                                )}
                                {candidate.averageAssessmentScore !== null && candidate.averageAssessmentScore !== undefined && (
                                  <div>
                                    <div className="text-xs text-blue-700 font-medium mb-1">Avg Score</div>
                                    <div className="text-lg font-bold text-blue-900">{Math.round(candidate.averageAssessmentScore)}%</div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Matched Skills */}
                              {candidate.matchedSkills && candidate.matchedSkills.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-blue-200">
                                  <div className="text-xs font-medium text-blue-900 mb-1.5">Matched Skills:</div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {candidate.matchedSkills.slice(0, 8).map((skill, idx) => (
                                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                        {skill}
                                      </span>
                                    ))}
                                    {candidate.matchedSkills.length > 8 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                        +{candidate.matchedSkills.length - 8} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Missing Skills */}
                              {candidate.missingSkills && candidate.missingSkills.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-blue-200">
                                  <div className="text-xs font-medium text-blue-900 mb-1.5">Missing Skills:</div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {candidate.missingSkills.slice(0, 5).map((skill, idx) => (
                                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                        {skill}
                                      </span>
                                    ))}
                                    {candidate.missingSkills.length > 5 && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                        +{candidate.missingSkills.length - 5} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Regular candidate details (for non-matched candidates) */}
                          {!selectedJobId && (
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              {candidate.experienceYears > 0 && (
                                <span className="text-xs text-gray-600">
                                  {candidate.experienceYears} {candidate.experienceYears === 1 ? 'year' : 'years'} experience
                                </span>
                              )}
                              {candidate.expectedSalary && (
                                <span className="text-xs text-gray-600">
                                  Expected Salary: {typeof candidate.expectedSalary === 'number' 
                                    ? candidate.expectedSalary.toLocaleString() 
                                    : candidate.expectedSalary}
                                </span>
                              )}
                              {candidate.availabilityDate && (
                                <span className="text-xs text-gray-600">
                                  Joining Date: {formatDate(candidate.availabilityDate)}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Assessment Scores */}
                          {!selectedJobId && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Assessments:</span>
                                {isLoadingScore ? (
                                  <span className="text-xs text-gray-500">Loading...</span>
                                ) : score && score.completedCount > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-900">
                                      <strong>{score.completedCount}</strong> completed
                                    </span>
                                    {score.assessments.length > 0 && (
                                      <div className="relative inline-block group">
                                        <svg 
                                          className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {/* Tooltip */}
                                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none">
                                          <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 min-w-[220px] max-w-[280px]">
                                            <div className="font-semibold mb-2 text-sm">Assessment Details:</div>
                                            <div className="space-y-2">
                                              {score.assessments.map((assessment, idx) => {
                                                const skillName = assessment.skill_name || assessment.title || 'Assessment';
                                                const scoreValue = assessment.score !== undefined ? Math.round(assessment.score) : null;
                                                const difficultyLevel = assessment.difficulty_level || assessment.difficultyLevel || '';
                                                const levelLabel = difficultyLevel ? difficultyLevel.charAt(0).toUpperCase() + difficultyLevel.slice(1) : '';
                                                return (
                                                  <div key={idx} className="flex items-center justify-between gap-3 border-b border-gray-700 last:border-b-0 last:pb-0 pb-2 last:pb-0">
                                                    <span className="flex-1">{skillName}</span>
                                                    <div className="flex items-center gap-2">
                                                      {levelLabel && <span className="text-gray-300 text-xs">{levelLabel}</span>}
                                                      {scoreValue !== null && <span className="font-semibold">{scoreValue}%</span>}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                          {/* Arrow */}
                                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">No assessments yet</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Assessment Scores for Matched Candidates */}
                          {selectedJobId && candidate.totalAssessments !== undefined && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Assessments:</span>
                                {candidate.totalAssessments > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-900">
                                      <strong>{candidate.totalAssessments}</strong> completed
                                    </span>
                                    {candidate.assessmentScores && candidate.assessmentScores.length > 0 && (
                                      <div className="relative inline-block group">
                                        <svg 
                                          className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {/* Tooltip */}
                                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none">
                                          <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 min-w-[220px] max-w-[280px]">
                                            <div className="font-semibold mb-2 text-sm">Assessment Details:</div>
                                            <div className="space-y-2">
                                              {candidate.assessmentScores.map((assessment, idx) => {
                                                const skillName = assessment.skillName || assessment.skill_name || 'Assessment';
                                                const scoreValue = assessment.score !== undefined ? Math.round(assessment.score) : null;
                                                const difficultyLevel = assessment.difficultyLevel || assessment.difficulty_level || '';
                                                const levelLabel = difficultyLevel ? difficultyLevel.charAt(0).toUpperCase() + difficultyLevel.slice(1) : '';
                                                return (
                                                  <div key={idx} className="flex items-center justify-between gap-3 border-b border-gray-700 last:border-b-0 last:pb-0 pb-2 last:pb-0">
                                                    <span className="flex-1">{skillName}</span>
                                                    <div className="flex items-center gap-2">
                                                      {levelLabel && <span className="text-gray-300 text-xs">{levelLabel}</span>}
                                                      {scoreValue !== null && <span className="font-semibold">{scoreValue}%</span>}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                          {/* Arrow */}
                                          <div className="absolute left-4 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">No assessments yet</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Approved Request - Show Full Details */}
                          {(() => {
                            const requestStatus = requestStatuses[candidate.id];
                            const details = candidateDetails[candidate.id];
                            if (requestStatus && requestStatus.status === 'approved' && details) {
                              return (
                                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="text-sm font-semibold text-green-900">Full Details Available (Request Approved)</h4>
                                  </div>
                                  
                                  <div className="space-y-2 text-sm">
                                    {details.candidateEmail && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">Email:</span>
                                        <a href={`mailto:${details.candidateEmail}`} className="text-primary-600 hover:underline">
                                          {details.candidateEmail}
                                        </a>
                                      </div>
                                    )}
                                    {details.phone && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">Phone:</span>
                                        <a href={`tel:${details.phone}`} className="text-primary-600 hover:underline">
                                          {details.phone}
                                        </a>
                                      </div>
                                    )}
                                    {details.resumeUrl && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">Resume:</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            downloadResumeAuthenticated(
                                              details.resumeUrl || details.resume_url
                                            ).catch(() => toast.error('Could not download resume'))
                                          }
                                          className="text-primary-600 hover:underline flex items-center gap-1"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          Download Resume
                                        </button>
                                      </div>
                                    )}
                                    {details.location && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">Location:</span>
                                        <span className="text-gray-600">{details.location}</span>
                                      </div>
                                    )}
                                    {details.experienceYears > 0 && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">Experience:</span>
                                        <span className="text-gray-600">{details.experienceYears} {details.experienceYears === 1 ? 'year' : 'years'}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Request Status Badge and View Details Button */}
                    <div className="flex items-center gap-2">
                      {(() => {
                        const requestStatus = requestStatuses[candidate.id];
                        if (requestStatus) {
                          const statusColors = {
                            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                            approved: 'bg-green-100 text-green-800 border-green-300',
                            rejected: 'bg-red-100 text-red-800 border-red-300'
                          };
                          const statusLabels = {
                            pending: 'Request Pending',
                            approved: 'Request Approved',
                            rejected: 'Request Rejected'
                          };
                          return (
                            <span className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${statusColors[requestStatus.status] || 'bg-gray-100 text-gray-800 border-gray-300'}`}>
                              {statusLabels[requestStatus.status] || requestStatus.status}
                            </span>
                          );
                        }
                        return null;
                      })()}
                      <button
                        onClick={() => handleViewCandidateDetails(candidate)}
                        className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors font-medium flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}{' '}
                candidates
                {hasActiveFilters && (
                  <span className="text-primary-600 ml-2">
                    ({filteredCandidates.length} matching filters)
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * pageSize >= total}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Candidate Detail Modal */}
      {selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCandidate(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900">
                {getCandidateName(selectedCandidate)}
              </h2>
              <button
                onClick={() => setSelectedCandidate(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {(() => {
                const requestStatus = requestStatuses[selectedCandidate.id];
                const isApproved = requestStatus && requestStatus.status === 'approved';
                const hasRequest = requestStatus && (requestStatus.status === 'pending' || requestStatus.status === 'rejected');
                
                if (loadingCandidateDetails[selectedCandidate.id] && !candidateDetails[selectedCandidate.id]) {
                  return (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                      <span className="ml-4 text-gray-600">Loading candidate details...</span>
                    </div>
                  );
                }
                
                const details = candidateDetails[selectedCandidate.id] || {};
                
                // Debug logging
                console.log('=== MODAL DEBUG ===');
                console.log('Modal - Candidate ID:', selectedCandidate.id);
                console.log('Modal - Request Status:', requestStatus);
                console.log('Modal - Is Approved:', isApproved);
                console.log('Modal - Details object:', details);
                console.log('Modal - Resume URL:', details.resumeUrl || details.resume_url);
                console.log('Modal - Email:', details.candidateEmail || details.candidate_email);
                console.log('Modal - Phone:', details.phone);
                console.log('Modal - All detail keys:', Object.keys(details));
                console.log('==================');
                
                return (
                  <>
                    {/* Warning message if request is pending/rejected (but not if no request) */}
                    {hasRequest && !isApproved && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <h3 className="text-sm font-semibold text-yellow-900">
                              {requestStatus.status === 'pending' ? 'Request Pending' : 'Request Rejected'}
                            </h3>
                            <p className="text-xs text-yellow-700 mt-1">
                              {requestStatus.status === 'pending' 
                                ? 'Your request is pending admin approval. Basic information is available below.'
                                : 'Your request was rejected. Basic information is available below.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resume Download Section - Prominent (Only when approved) */}
                    {isApproved && (
                      <>
                        {(details.resumeUrl || details.resume_url) ? (
                          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <div>
                                  <h3 className="text-lg font-semibold text-green-900">Resume Available</h3>
                                  <p className="text-sm text-green-700">Download the candidate's resume to view full details</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  downloadResumeAuthenticated(
                                    details.resumeUrl || details.resume_url
                                  ).catch(() => toast.error('Could not download resume'))
                                }
                                className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Resume
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className="text-sm text-yellow-800">Resume not available for this candidate.</p>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Basic Candidate Info (Always visible) */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Basic Information</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <strong>Name:</strong> {details.candidateName || getCandidateName(selectedCandidate)}
                        </div>
                        {details.title && (
                          <div>
                            <strong>Title:</strong> {details.title}
                          </div>
                        )}
                        {details.location && (
                          <div>
                            <strong>Location:</strong> {details.location}
                          </div>
                        )}
                        {details.address && !details.location && (
                          <div>
                            <strong>Address:</strong> {details.address}
                          </div>
                        )}
                        {details.experienceYears > 0 && (
                          <div>
                            <strong>Experience:</strong> {details.experienceYears} {details.experienceYears === 1 ? 'year' : 'years'}
                          </div>
                        )}
                        {/* Contact Details Section - Only when approved */}
                        {isApproved && (
                          <div className="col-span-2 pt-2 border-t border-gray-200">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Contact Information (Approved Request):</div>
                            <div className="space-y-2">
                              {(details.candidateEmail || details.candidate_email) && (
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <strong className="text-sm">Email:</strong> <a href={`mailto:${details.candidateEmail || details.candidate_email}`} className="text-primary-600 hover:underline text-sm">{details.candidateEmail || details.candidate_email}</a>
                                </div>
                              )}
                              {details.phone && (
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  <strong className="text-sm">Phone:</strong> <a href={`tel:${details.phone}`} className="text-primary-600 hover:underline text-sm">{details.phone}</a>
                                </div>
                              )}
                              {!details.candidateEmail && !details.candidate_email && !details.phone && (
                                <div className="text-sm text-gray-500 italic">
                                  Contact information not available
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {details.availableFrom && (
                          <div>
                            <strong>Available From:</strong> {formatDate(details.availableFrom)}
                          </div>
                        )}
                      </div>
                      {details.bio && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <strong className="block mb-1">Bio:</strong>
                          <p className="text-sm text-gray-700">{details.bio}</p>
                        </div>
                      )}
                      {details.summary && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <strong className="block mb-1">Summary:</strong>
                          <p className="text-sm text-gray-700">{details.summary}</p>
                        </div>
                      )}
                    </div>

                    {/* Education Details */}
                      {details.education && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                            Education
                          </h3>
                          <div className="space-y-3">
                            {Array.isArray(details.education) ? (
                              details.education.map((edu, idx) => (
                                <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-4 border-primary-500">
                                  <div className="font-semibold text-gray-900 text-base">
                                    {edu.degree && <span>{edu.degree}</span>}
                                    {edu.fieldOfStudy && <span> in {edu.fieldOfStudy}</span>}
                                  </div>
                                  {edu.institution && (
                                    <div className="text-sm text-gray-700 mt-1">{edu.institution}</div>
                                  )}
                                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                                    {edu.year && <span>Year: {edu.year}</span>}
                                    {edu.gpa && <span>GPA: {edu.gpa}</span>}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-primary-500">
                                <p className="text-sm text-gray-700 whitespace-pre-line">{details.education}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Technical Skills (Always visible) */}
                    {((details.technicalSkills && details.technicalSkills.length > 0) || 
                      (details.skills && details.skills && details.skills.length > 0)) && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            Technical Skills
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {details.technicalSkills && details.technicalSkills.length > 0 ? (
                              details.technicalSkills.map((skill, idx) => (
                                <div key={idx} className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
                                  <div className="font-medium text-primary-900">{skill.name}</div>
                                  {skill.proficiency && (
                                    <div className="text-xs text-primary-700 mt-0.5">
                                      {skill.proficiency.charAt(0).toUpperCase() + skill.proficiency.slice(1)}
                                      {skill.yearsOfExperience && ` • ${skill.yearsOfExperience} years`}
                                    </div>
                                  )}
                                  {skill.isCertified && (
                                    <div className="text-xs text-green-700 mt-0.5">✓ Certified</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              details.skills.map((skill, idx) => (
                                <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-sm">
                                  {skill}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Current Company Highlight */}
                      {details.currentCompany && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Current Position
                          </h3>
                          <div className="text-xl font-semibold text-gray-900">
                            {details.currentRole || 'Current Role'}
                          </div>
                          <div className="text-base text-gray-700 mt-1">
                            at {details.currentCompany}
                          </div>
                          {details.currentResponsibilities && details.currentResponsibilities.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-blue-200">
                              <div className="text-sm font-medium text-blue-900 mb-2">Current Responsibilities:</div>
                              <ul className="space-y-2">
                                {details.currentResponsibilities.map((resp, idx) => (
                                  <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                                    <span className="text-blue-600 mt-1.5">•</span>
                                    <span>{resp}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Experience */}
                      {details.experience && details.experience.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Work Experience
                          </h3>
                          <div className="space-y-4">
                            {details.experience.map((exp, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-4 border-primary-500">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900 text-lg">
                                      {exp.position || 'Position'}
                                    </div>
                                    <div className="text-sm text-gray-700 mt-1">
                                      {exp.company || 'Company'}
                                      {exp.location && <span className="text-gray-500"> • {exp.location}</span>}
                                    </div>
                                    {(exp.startDate || exp.endDate) && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {exp.startDate || 'Start'} - {exp.endDate || 'Present'}
                                        {exp.duration && <span> • {exp.duration}</span>}
                                      </div>
                                    )}
                                    {exp.isCurrent && (
                                      <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Roles and Responsibilities */}
                                {exp.responsibilities && exp.responsibilities.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-sm font-medium text-gray-700 mb-2">Roles & Responsibilities:</div>
                                    <ul className="space-y-1.5">
                                      {exp.responsibilities.map((resp, respIdx) => (
                                        <li key={respIdx} className="text-sm text-gray-600 flex items-start gap-2">
                                          <span className="text-primary-600 mt-1.5">•</span>
                                          <span>{resp}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {Array.isArray(details.projects) && details.projects.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Projects</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            {details.projects.map((project, index) => (
                              <li key={`project-${index}`} className="text-sm text-gray-700">{project}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(details.certifications) && details.certifications.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Certifications</h3>
                          <ul className="list-disc pl-5 space-y-1">
                            {details.certifications.map((item, index) => (
                              <li key={`cert-${index}`} className="text-sm text-gray-700">{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(details.languages) && details.languages.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Languages</h3>
                          <p className="text-sm text-gray-700">{details.languages.join(', ')}</p>
                        </div>
                      )}

                      {/* Assessment Scores Summary */}
                      {details.totalAssessments > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                            Assessment Results
                          </h3>
                          <div className="grid grid-cols-2 gap-6 mb-4">
                            <div>
                              <div className="text-sm text-green-700 mb-1">Total Assessments</div>
                              <div className="text-3xl font-bold text-green-900">{details.totalAssessments}</div>
                            </div>
                            {details.averageAssessmentScore !== null && (
                              <div>
                                <div className="text-sm text-green-700 mb-1">Average Score</div>
                                <div className="text-3xl font-bold text-green-900">
                                  {Math.round(details.averageAssessmentScore)}%
                                </div>
                              </div>
                            )}
                          </div>
                          {details.assessmentScores && details.assessmentScores.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-green-200">
                              <div className="text-sm font-medium text-green-900 mb-3">Assessment Breakdown:</div>
                              <div className="space-y-2">
                                {details.assessmentScores.map((assessment, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm bg-white rounded-lg p-2">
                                    <span className="text-green-800 font-medium">{assessment.skillName}</span>
                                    <div className="flex items-center gap-2">
                                      {assessment.difficultyLevel && (
                                        <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                                          {assessment.difficultyLevel.charAt(0).toUpperCase() + assessment.difficultyLevel.slice(1)}
                                        </span>
                                      )}
                                      <span className="font-semibold text-green-900">{assessment.score}%</span>
                                      {assessment.passed && (
                                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">Passed</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateList;
